
import { useState, useCallback } from 'react';
import { A2AMessage } from '@/utils/a2a/types';
import { sendA2AMessage } from '@/utils/a2a/messaging';
import { toast } from 'sonner';
import { saveContext, loadContext } from '@/utils/a2a/context/contextManager';

export const useA2AMessageHandler = () => {
  const [pendingMessages, setPendingMessages] = useState<A2AMessage[]>([]);
  const [agentResponses, setAgentResponses] = useState<A2AMessage[]>([]);
  const [messageStats, setMessageStats] = useState({
    sent: 0,
    received: 0,
    failed: 0,
    lastActivity: new Date()
  });

  const handleContextMessage = async (message: A2AMessage) => {
    try {
      if (message.metadata.messageType === 'CONTEXT_SAVE') {
        const contextPayload = {
          key: message.metadata.contextKey || `context-${Date.now()}`,
          content: message.content,
          ttl: message.metadata.ttl,
          metadata: message.metadata.contextData || {},
          chunk_type: message.metadata.contextType || 'general'
        };
        
        const result = await saveContext(contextPayload);
        
        // Send response back to requesting agent
        return sendA2AMessage({
          fromAgent: message.toAgent,
          toAgent: message.fromAgent,
          content: result.success ? 'Context saved successfully' : `Context save failed: ${result.error}`,
          signature: '',
          keyId: '',
          metadata: {
            protocol: 'A2A',
            version: '1.1',
            messageType: 'RESPONSE',
            conversationId: message.metadata.conversationId,
            inReplyTo: message.id,
            contextOperation: 'save',
            contextKey: contextPayload.key,
            success: result.success,
            error: result.error
          }
        });
      }
      
      if (message.metadata.messageType === 'CONTEXT_LOAD') {
        const key = message.metadata.contextKey;
        if (!key) {
          throw new Error('Missing context key');
        }
        
        const result = await loadContext(key);
        
        // Fixed: Added null check for metadata and ensured it's properly handled
        const contextData = result.metadata || {};
        
        // Send response back to requesting agent
        return sendA2AMessage({
          fromAgent: message.toAgent,
          toAgent: message.fromAgent,
          content: result.content || 'Context not found',
          signature: '',
          keyId: '',
          metadata: {
            protocol: 'A2A',
            version: '1.1',
            messageType: 'RESPONSE',
            conversationId: message.metadata.conversationId,
            inReplyTo: message.id,
            contextOperation: 'load',
            contextKey: key,
            contextData: contextData,
            success: result.success,
            error: result.error
          }
        });
      }
    } catch (error) {
      console.error("Error handling context message:", error);
      return sendA2AMessage({
        fromAgent: message.toAgent,
        toAgent: message.fromAgent,
        content: `Error processing context operation: ${error instanceof Error ? error.message : String(error)}`,
        signature: '',
        keyId: '',
        metadata: {
          protocol: 'A2A',
          version: '1.1',
          messageType: 'RESPONSE',
          conversationId: message.metadata.conversationId,
          inReplyTo: message.id,
          contextOperation: message.metadata.messageType === 'CONTEXT_SAVE' ? 'save' : 'load',
          success: false,
          error: error instanceof Error ? error.message : String(error)
        }
      });
    }
  };

  const sendMessage = async (
    message: Omit<A2AMessage, 'id' | 'timestamp'>,
    statsOpts?: { priority: A2AMessage['metadata']['priority'] }
  ): Promise<A2AMessage> => {
    try {
      console.log(`Sending message to ${message.toAgent}: ${message.content.substring(0, 50)}...`);
      
      const sent = await sendA2AMessage(message);
      
      setPendingMessages(prev => [...prev, sent]);
      setMessageStats(prev => ({
        ...prev,
        sent: prev.sent + 1,
        lastActivity: new Date()
      }));
      
      return sent;
    } catch (error) {
      console.error("Error in sendMessage:", error);
      setMessageStats(prev => ({
        ...prev,
        failed: prev.failed + 1,
        lastActivity: new Date()
      }));
      toast.error("Failed to send message to agent");
      throw error;
    }
  };

  const cleanupOldMessages = useCallback(() => {
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
    setPendingMessages(prev => 
      prev.filter(msg => new Date(msg.timestamp) > thirtyMinutesAgo)
    );
  }, []);

  const broadcastNotification = useCallback((from: string, content: string, type: 'info' | 'warning' | 'error' | 'success' = 'info', targets?: string[]) => {
    const priority = type === 'error' ? 'high' : type === 'warning' ? 'normal' : 'low';
    
    sendA2AMessage({
      fromAgent: from,
      toAgent: targets ? targets[0] : '*',
      content,
      signature: '',
      keyId: '',
      metadata: {
        protocol: 'A2A',
        version: '1.1',
        messageType: 'NOTIFICATION',
        priority: priority as any,
        notificationType: type,
        broadcast: !targets,
        recipients: targets || [],
        timestamp: new Date().toISOString()
      }
    });
    
    // Show UI toast
    if (type === 'error') toast.error(content);
    else if (type === 'warning') toast.warning(content);
    else if (type === 'success') toast.success(content);
    else toast.info(content);
    
  }, []);

  return {
    pendingMessages,
    agentResponses,
    sendMessage,
    setPendingMessages,
    setAgentResponses,
    cleanupOldMessages,
    messageStats,
    broadcastNotification,
    handleContextMessage
  };
};
